<script setup>
// Seletor de agendas do Google (spec 010, R-23): onde gravar os eventos (uma agenda) e quais agendas contam como
// ocupado. A listagem exige o escopo calendarlist.readonly, pedido so aqui (consentimento incremental): sem ele a
// janela oferece "Autorizar" e o Google devolve a pessoa para a tela de origem.
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import GoogleCalendarAPI from 'dashboard/api/googleCalendar';
import { openGoogleConsent } from '../composables/openGoogleConsent';

const props = defineProps({
  connectionId: { type: Number, required: true },
  practitionerId: { type: Number, default: null },
});
const emit = defineEmits(['saved']);

const { t } = useI18n();
const dialog = ref(null);
const loading = ref(false);
const saving = ref(false);
const canList = ref(false);
const calendars = ref([]);
const destinationId = ref(null);
const readIds = ref([]);

const open = async () => {
  dialog.value.open();
  loading.value = true;
  try {
    const { data } = await GoogleCalendarAPI.getCalendars(props.connectionId);
    canList.value = data.data.can_list;
    calendars.value = data.data.calendars;
    destinationId.value = calendars.value.find(c => c.destination)?.id ?? null;
    readIds.value = calendars.value.filter(c => c.read).map(c => c.id);
  } catch {
    dialog.value.close();
    useAlert(t('GOOGLE_CALENDAR.PICKER.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

// A permissao de listar agendas e concedida em outra aba, que abre o seletor ao voltar do Google.
const authorize = async () => {
  saving.value = true;
  try {
    await openGoogleConsent(async () => {
      const { data } = await GoogleCalendarAPI.authorize({
        practitionerId: props.practitionerId,
        calendarList: true,
      });
      return data.url;
    });
    dialog.value.close();
  } catch {
    useAlert(t('GOOGLE_CALENDAR.ACTION_ERROR'));
  } finally {
    saving.value = false;
  }
};

const toggleRead = id => {
  readIds.value = readIds.value.includes(id)
    ? readIds.value.filter(item => item !== id)
    : [...readIds.value, id];
};

const save = async () => {
  saving.value = true;
  try {
    await GoogleCalendarAPI.saveCalendars(props.connectionId, {
      calendarId: destinationId.value,
      readCalendarIds: readIds.value,
    });
    useAlert(t('GOOGLE_CALENDAR.PICKER.SAVED'));
    dialog.value.close();
    emit('saved');
  } catch {
    useAlert(t('GOOGLE_CALENDAR.PICKER.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialog"
    :title="t('GOOGLE_CALENDAR.PICKER.TITLE')"
    :description="t('GOOGLE_CALENDAR.PICKER.DESCRIPTION')"
    :confirm-button-label="t('GOOGLE_CALENDAR.PICKER.SAVE')"
    :show-confirm-button="canList && !loading"
    :disable-confirm-button="!destinationId"
    :is-loading="saving"
    @confirm="save"
  >
    <p v-if="loading" class="text-sm text-n-slate-11">
      {{ t('GOOGLE_CALENDAR.PICKER.LOADING') }}
    </p>
    <div
      v-else-if="!canList"
      class="flex flex-col items-start gap-3"
      data-test="google-calendar-picker-consent"
    >
      <p class="text-sm text-n-slate-11">
        {{ t('GOOGLE_CALENDAR.PICKER.CONSENT_HINT') }}
      </p>
      <Button
        size="sm"
        icon="i-lucide-shield-check"
        :label="t('GOOGLE_CALENDAR.PICKER.AUTHORIZE')"
        :is-loading="saving"
        data-test="google-calendar-picker-authorize"
        @click="authorize"
      />
    </div>
    <div v-else class="flex flex-col gap-5" data-test="google-calendar-picker">
      <fieldset class="flex flex-col gap-2">
        <legend class="mb-1 text-sm font-medium text-n-slate-12">
          {{ t('GOOGLE_CALENDAR.PICKER.WRITE_TO') }}
        </legend>
        <label
          v-for="calendar in calendars.filter(c => c.writable)"
          :key="`w-${calendar.id}`"
          class="flex items-center gap-2 text-sm text-n-slate-12"
        >
          <input
            v-model="destinationId"
            type="radio"
            name="google-calendar-destination"
            class="!mb-0"
            :value="calendar.id"
            :data-test="`destination-${calendar.id}`"
          />
          {{ calendar.summary }}
        </label>
        <p class="mb-0 text-xs text-n-slate-10">
          {{ t('GOOGLE_CALENDAR.PICKER.WRITE_TO_HINT') }}
        </p>
      </fieldset>
      <fieldset class="flex flex-col gap-2">
        <legend class="mb-1 text-sm font-medium text-n-slate-12">
          {{ t('GOOGLE_CALENDAR.PICKER.READ_FROM') }}
        </legend>
        <label
          v-for="calendar in calendars"
          :key="`r-${calendar.id}`"
          class="flex items-center gap-2 text-sm text-n-slate-12"
        >
          <input
            type="checkbox"
            class="!mb-0"
            :checked="readIds.includes(calendar.id)"
            :data-test="`read-${calendar.id}`"
            @change="toggleRead(calendar.id)"
          />
          {{ calendar.summary }}
        </label>
        <p class="mb-0 text-xs text-n-slate-10">
          {{ t('GOOGLE_CALENDAR.PICKER.READ_FROM_HINT') }}
        </p>
      </fieldset>
    </div>
  </Dialog>
</template>
